/**
 * On-chain Fluid DEX state — the share↔token conversion for SMART vaults.
 *
 * A T2/T3/T4 vault holds its collateral and/or debt as Fluid DEX **LP shares**.
 * `VaultResolver` reports those sides' totals (and `positionsByUser` reports
 * every user position) as a 1e18 share count, so nothing about a smart vault
 * can be sized — no token amount, no USD, no health factor — without
 * `token{0,1}Per{Supply,Borrow}Share` from the DEX.
 *
 * WHY ON-CHAIN AND NOT THE API: the Fluid API's `/vaults` route serves only its
 * LISTED vaults. On Ethereum that is 43 of the 80 smart vaults actually
 * deployed, and 21 of the 37 it omits hold real balances (measured 2026-08-14).
 * Sourcing the share math from the API would therefore have silently dropped
 * live positions — the exact failure this integration already had. Reading the
 * DEX directly covers every deployed pool (see the union in
 * `fetchFluidDexState` — the resolver's own enumeration is NOT sufficient).
 *
 * The API is still required for the DEX **trading yield** (see `apiData.ts`),
 * which is genuinely not published on-chain anywhere.
 */
import { FluidDexResolverAbi } from '@1delta/abis'
import { fluidResolvers } from '@1delta/data-sdk'
import { MulticallRetryFunction } from '../../../types'

/**
 * DexResolver deployments, verified live on every Fluid chain (2026-08-14).
 *
 * Hard-coded because `config/fluid-resolvers.json` in `lender-metadata` does
 * not carry a `dexResolver` field yet; the lookup prefers that field the moment
 * it appears, so publishing metadata later needs no code change here.
 *
 * NOTE the two deployment groups differ only by the deployer argument used at
 * construction — the ABI is identical.
 */
const DEX_RESOLVER_FALLBACK: { [chainId: string]: string } = {
  '1': '0x11D80CfF056Cef4F9E6d23da8672fE9873e5cC07',
  '137': '0x11D80CfF056Cef4F9E6d23da8672fE9873e5cC07',
  '8453': '0x11D80CfF056Cef4F9E6d23da8672fE9873e5cC07',
  '42161': '0x11D80CfF056Cef4F9E6d23da8672fE9873e5cC07',
  '56': '0xAf572EfC84d905926F7b05C1B7bE04e4E89542B0',
  '9745': '0xAf572EfC84d905926F7b05C1B7bE04e4E89542B0',
}

export const dexResolverFor = (chainId: string): string | undefined =>
  (fluidResolvers()?.[chainId] as any)?.dexResolver ??
  DEX_RESOLVER_FALLBACK[chainId]

/** Per-1e18-share token amounts for one DEX pool, each in its OWN token decimals. */
export interface FluidDexShareState {
  dex: string
  token0PerSupplyShare: bigint
  token1PerSupplyShare: bigint
  token0PerBorrowShare: bigint
  token1PerBorrowShare: bigint
  totalSupplyShares: bigint
  totalBorrowShares: bigint
}

export type FluidDexStateMap = { [dexAddress: string]: FluidDexShareState }

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'

const TTL_MS = 60_000

type CacheEntry = { at: number; data: FluidDexStateMap | undefined }

const cache: { [chainId: string]: CacheEntry | undefined } = {}
const inflight = new Map<string, Promise<FluidDexStateMap | undefined>>()

/**
 * Read every DEX pool's share state for a chain.
 *
 * Two multicall rounds: discover the pools, then read each one's state. The
 * combined `getAllDexEntireDatas()` exists but returns a 205-word struct per
 * pool and times out on Ethereum's 48 pools against public RPCs, so the
 * narrower `getDexState` is used instead.
 *
 * DISCOVERY IS A UNION, and it has to be. `getAllDexAddresses()` derives pool
 * addresses by CREATE2 from ONE deployer, so it misses an older DEX generation
 * that is still backing live vaults — on Ethereum three such pools serve 10
 * smart vaults, and they answer `getDexState` perfectly well (they even reuse
 * dexIds 1-3, so the two generations collide in id space and only the ADDRESS
 * identifies a pool). Vault-referenced addresses are therefore folded in via
 * `extraDexAddresses`; the caller sources them from each smart vault's
 * `constantVariables.supply` / `.borrow`.
 */
export const fetchFluidDexState = async (
  chainId: string,
  multicallRetry: MulticallRetryFunction,
  extraDexAddresses: string[] = [],
): Promise<FluidDexStateMap | undefined> => {
  const resolver = dexResolverFor(chainId)
  if (!resolver) return undefined

  const hit = cache[chainId]
  if (hit && Date.now() - hit.at < TTL_MS) return hit.data
  const pending = inflight.get(chainId)
  if (pending) return pending

  const run = (async () => {
    try {
      const [addresses] = (await multicallRetry({
        chain: chainId,
        calls: [{ address: resolver, name: 'getAllDexAddresses', params: [] }],
        abi: [FluidDexResolverAbi],
      } as any)) as any[]

      const list: string[] = [
        ...new Set(
          [...((addresses ?? []) as string[]), ...extraDexAddresses]
            .filter((a) => !!a && a !== ZERO_ADDRESS)
            .map((a) => a.toLowerCase()),
        ),
      ]
      if (!list.length) throw new Error('no dex addresses')

      const states = (await multicallRetry({
        chain: chainId,
        calls: list.map((dex) => ({
          address: resolver,
          name: 'getDexState',
          params: [dex],
        })),
        abi: list.map(() => FluidDexResolverAbi),
      } as any)) as any[]

      const data: FluidDexStateMap = {}
      for (let i = 0; i < list.length; i++) {
        const s = states?.[i]
        // A failed entry decodes to undefined/'0x'; skip rather than zero it,
        // so the parser drops that vault instead of pricing it at nothing.
        if (!s || s.token0PerSupplyShare === undefined) continue
        data[list[i]] = {
          dex: list[i],
          token0PerSupplyShare: BigInt(s.token0PerSupplyShare ?? 0),
          token1PerSupplyShare: BigInt(s.token1PerSupplyShare ?? 0),
          token0PerBorrowShare: BigInt(s.token0PerBorrowShare ?? 0),
          token1PerBorrowShare: BigInt(s.token1PerBorrowShare ?? 0),
          totalSupplyShares: BigInt(s.totalSupplyShares ?? 0),
          totalBorrowShares: BigInt(s.totalBorrowShares ?? 0),
        }
      }
      cache[chainId] = { at: Date.now(), data }
      return data
    } catch {
      const prev = cache[chainId]?.data
      cache[chainId] = { at: Date.now(), data: prev }
      return prev
    } finally {
      inflight.delete(chainId)
    }
  })()

  inflight.set(chainId, run)
  return run
}

/** Synchronous read of whatever `fetchFluidDexState` last cached for a chain. */
export const getCachedFluidDexState = (
  chainId: string,
): FluidDexStateMap | undefined => cache[chainId]?.data

/** Test seam — drop cached DEX state. */
export const __resetFluidDexStateCache = () => {
  for (const k of Object.keys(cache)) delete cache[k]
}

/** Test seam — inject DEX state without a network round-trip. */
export const __seedFluidDexStateCache = (
  chainId: string,
  data: FluidDexStateMap,
) => {
  cache[chainId] = { at: Date.now(), data }
}
